// Finalisté A/B/C + uložení rozhodnutí focusu.
import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Medal, FloppyDisk } from "@phosphor-icons/react";
import { orderFinalists, saveDecision, patchCandidate, cancelDecision } from "../../lib/lab-api";

const RANK_LABEL = ["A", "B", "C"];
const OUTCOMES = [
    { key: "BUY_NOW", label: "Koupit hned" }, { key: "WAIT", label: "Počkat" },
    { key: "MONITOR_TEAM_NEWS", label: "Sledovat team news" }, { key: "ADD_TO_SCENARIO", label: "Do scénáře" },
    { key: "PLANNED_TRANSFER", label: "Plánovaný přestup" }, { key: "REJECT_FOCUS", label: "Zamítnout focus" },
];

export default function FocusFinalists({ focus, candidates, onChanged }) {
    const finalists = candidates.filter((c) => c.status === "FINALIST")
        .sort((a, b) => (a.finalist_rank || 9) - (b.finalist_rank || 9));
    const [reason, setReason] = useState("");
    const [outcome, setOutcome] = useState("BUY_NOW");
    const [plannedGw, setPlannedGw] = useState("");
    const [busy, setBusy] = useState(false);
    const decision = focus.decision;

    const move = async (idx, dir) => {
        const next = [...finalists];
        const j = idx + dir;
        if (j < 0 || j >= next.length) return;
        [next[idx], next[j]] = [next[j], next[idx]];
        try { await orderFinalists(focus.id, next.map((c) => c.id)); onChanged?.(); }
        catch (e) { toast.error(e.response?.data?.detail || "Změna pořadí selhala"); }
    };

    const backToScout = async (c) => {
        try { await patchCandidate(focus.id, c.id, { status: "SCOUTING" }); onChanged?.(); }
        catch (e) { toast.error(e.response?.data?.detail || "Vrácení selhalo"); }
    };

    const decide = async () => {
        if (!finalists.length) { toast.error("Nejdřív vyber finalisty ve Scout fázi"); return; }
        if (reason.trim().length < 3) { toast.error("Napiš důvod výběru"); return; }
        setBusy(true);
        try {
            await saveDecision(focus.id, {
                main_cid: finalists[0].id,
                alt1_cid: finalists[1]?.id || null,
                alt2_cid: finalists[2]?.id || null,
                reason, outcome,
                planned_gw: plannedGw ? Number(plannedGw) : null,
            });
            toast.success("Rozhodnutí uloženo");
            onChanged?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
        finally { setBusy(false); }
    };

    if (decision) {
        const others = candidates
            .filter((c) => ["SELECTED", "ALTERNATIVE", "FINALIST"].includes(c.status))
            .sort((a, b) => ({ SELECTED: 0, ALTERNATIVE: 1, FINALIST: 2 }[a.status] - { SELECTED: 0, ALTERNATIVE: 1, FINALIST: 2 }[b.status]));
        const badge = { SELECTED: ["Hlavní volba", "border-fa-mint text-fa-mint bg-fa-mint/10"], ALTERNATIVE: ["Alternativa", "border-white/40 text-white/80"], FINALIST: ["Finalista", "border-fa-yellow/60 text-fa-yellow bg-fa-yellow/10"] };
        return (
            <div className="space-y-3">
                <div className="border border-fa-mint/40 bg-fa-mint/5 p-4 space-y-2" data-testid="focus-decision-done">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-fa-mint flex items-center gap-1.5">
                        <Medal size={12} weight="fill" /> Rozhodnutí — {OUTCOMES.find((o) => o.key === decision.outcome)?.label || decision.outcome}
                    </div>
                    <div className="font-display font-black text-xl">{decision.main.name}</div>
                    {decision.alternatives?.length > 0 && (
                        <div className="font-mono text-[10px] text-white/60">
                            Alternativy: {decision.alternatives.map((a, i) => `${RANK_LABEL[i + 1]} — ${a.name}`).join(" · ")}
                        </div>
                    )}
                    <p className="font-mono text-[10px] text-white/70">„{decision.reason}"</p>
                    {decision.planned_gw && <p className="font-mono text-[9px] text-white/45">Plánované GW: {decision.planned_gw}</p>}
                    <button type="button" data-testid="cancel-decision-btn"
                            onClick={async () => {
                                try { await cancelDecision(focus.id); toast.success("Rozhodnutí zrušeno — hráči jsou zpět mezi finalisty"); onChanged?.(); }
                                catch (e) { toast.error(e.response?.data?.detail || "Zrušení selhalo"); }
                            }}
                            className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border border-fa-yellow/50 text-fa-yellow hover:bg-fa-yellow hover:text-black transition-colors">
                        ↩ Zrušit rozhodnutí (vrátit mezi finalisty)
                    </button>
                </div>
                {others.length > 0 && (
                    <div className="space-y-1.5" data-testid="decision-finalists-list">
                        <p className="font-mono text-[8px] uppercase tracking-widest text-white/35">Finalisté ve focusu ({others.length}) — zůstávají ke sledování</p>
                        {others.map((c) => (
                            <div key={c.id} className="border border-white/15 bg-[#0d0d0d] p-2.5 flex items-center gap-2 flex-wrap" data-testid={`finalist-${c.fpl_id}`}>
                                <span className={`font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border ${badge[c.status][1]}`}>{badge[c.status][0]}</span>
                                <span className="font-display font-bold text-sm">{c.name}</span>
                                <span className="font-mono text-[9px] text-white/50">{c.team} · {c.position} · £{c.price}</span>
                                {c.original_fit_score != null && <span className="font-mono text-[9px] text-fa-mint ml-auto">fit {Math.round(c.original_fit_score)}</span>}
                                {c.status === "ALTERNATIVE" && (
                                    <button type="button" data-testid={`alt-to-finalist-${c.fpl_id}`}
                                            onClick={async () => {
                                                try { await patchCandidate(focus.id, c.id, { status: "FINALIST" }); onChanged?.(); }
                                                catch (e) { toast.error(e.response?.data?.detail || "Nepovedlo se"); }
                                            }}
                                            className="font-mono text-[8px] uppercase px-2 py-1 border border-fa-yellow/40 text-fa-yellow hover:bg-fa-yellow hover:text-black">
                                        → Finalista
                                    </button>
                                )}
                                {c.status === "FINALIST" && (
                                    <button type="button" onClick={() => backToScout(c)} className="font-mono text-[8px] uppercase px-2 py-1 border border-white/15 text-white/45 hover:text-white" data-testid={`finalist-back-${c.fpl_id}`}>
                                        ← Scout
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="focus-finalists">
            {finalists.length === 0 && (
                <p className="font-mono text-[11px] text-white/45 border border-white/10 bg-black/30 p-4">
                    Zatím žádní finalisté — ve Scout fázi dej kandidátům verdikt „→ Finalista" (max 3).
                </p>
            )}
            {finalists.map((c, i) => (
                <div key={c.id} className={`border p-3 flex items-center gap-3 ${i === 0 ? "border-fa-mint/50 bg-fa-mint/5" : "border-white/15 bg-[#0d0d0d]"}`}
                     data-testid={`finalist-${c.fpl_id}`}>
                    <div className={`font-display font-black text-2xl w-8 text-center ${i === 0 ? "text-fa-mint" : "text-white/40"}`}>{RANK_LABEL[i]}</div>
                    <div className="flex-1">
                        <div className="font-display font-bold text-sm">{c.name} {c.is_my_tip && <span className="font-mono text-[8px] px-1 border border-fa-yellow/60 text-fa-yellow uppercase align-middle">Můj tip</span>}</div>
                        <div className="font-mono text-[9px] text-white/50">{c.team} · {c.position} · £{c.price} · Original Fit {Math.round(c.original_fit_score || 0)}</div>
                    </div>
                    <div className="flex flex-col gap-0.5">
                        <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-white/40 hover:text-fa-mint disabled:opacity-20" data-testid={`finalist-up-${c.fpl_id}`}><ArrowUp size={13} /></button>
                        <button type="button" onClick={() => move(i, 1)} disabled={i === finalists.length - 1} className="text-white/40 hover:text-fa-mint disabled:opacity-20" data-testid={`finalist-down-${c.fpl_id}`}><ArrowDown size={13} /></button>
                    </div>
                    <button type="button" onClick={() => backToScout(c)} className="font-mono text-[8px] uppercase px-2 py-1 border border-white/15 text-white/45 hover:text-white" data-testid={`finalist-back-${c.fpl_id}`}>
                        ← Scout
                    </button>
                </div>
            ))}
            {finalists.length > 0 && (
                <div className="border border-white/15 bg-[#0d0d0d] p-3 space-y-2" data-testid="decision-form">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-fa-mint">Rozhodnutí — hlavní volba: {finalists[0].name}</div>
                    <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                              placeholder="Důvod výběru… (povinné)"
                              className="w-full bg-black/40 border border-white/25 px-2 py-1.5 font-mono text-[11px] text-white focus:outline-none focus:border-fa-mint placeholder:text-white/35"
                              data-testid="decision-reason" />
                    <div className="flex gap-2 flex-wrap items-center">
                        <select value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="decision-outcome"
                                className="bg-black/40 border border-white/25 px-2 py-1.5 font-mono text-[10px] text-white focus:outline-none">
                            {OUTCOMES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                        </select>
                        <input value={plannedGw} onChange={(e) => setPlannedGw(e.target.value)} placeholder="GW…" type="number" min="1" max="38"
                               className="bg-black/40 border border-white/25 px-2 py-1.5 font-mono text-[10px] text-white w-20 focus:outline-none"
                               data-testid="decision-gw" />
                        <button type="button" onClick={decide} disabled={busy} data-testid="decision-save"
                                className="font-mono text-[9px] uppercase tracking-widest px-4 py-2 border border-fa-mint text-fa-mint hover:bg-fa-mint hover:text-black font-bold flex items-center gap-1.5 disabled:opacity-40 ml-auto">
                            <FloppyDisk size={11} /> Uložit rozhodnutí
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
